import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Check, ArrowLeft, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Join() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [source, setSource] = useState('direct');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const s = params.get('source');
    if (s) setSource(s);
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim();
    // Light client-side format check — backend does authoritative validation
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: trimmed,
        name: name.trim() || undefined,
        source: source || 'direct',
        company: company || undefined,
      });
      setDone(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError("You're going a bit fast — please wait a moment and try again.");
      } else if (status === 400 || status === 422) {
        setError('Please enter a valid email address.');
      } else {
        setError("Something didn't go through. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 sm:px-6 pt-8 pb-16 flex-1 flex flex-col justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-foreground mb-6 transition-colors text-sm w-fit"
          data-testid="join-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Early Access
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-3" data-testid="join-title">
          Join the Photog Bizkit early access list
        </h1>
        <p className="text-text-secondary mb-8 leading-relaxed">
          The photography business, in your pocket — clients, bookings, invoices, portals. Drop your email and we'll open a spot for you when the beta widens.
        </p>

        {done ? (
          <div
            className="bg-success/10 border border-success/30 rounded-2xl p-6 flex items-start gap-3"
            data-testid="join-success"
          >
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">You're on the list.</p>
              <p className="text-sm text-text-secondary">
                We'll be in touch when the beta opens up. Watch your inbox — we just sent a quick confirmation.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="join-form" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-muted mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onInput={(e) => setEmail(e.target.value)}
                  placeholder="you@studio.com"
                  className="w-full bg-surface-raised border border-border rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                  data-testid="join-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text-muted mb-1">
                Name <span className="text-text-muted">(optional)</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onInput={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                data-testid="join-name-input"
              />
            </div>

            {/* Honeypot — hidden from real users; bots will fill it */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
            >
              <label htmlFor="company">Company (leave blank)</label>
              <input
                id="company"
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                data-testid="join-honeypot"
              />
            </div>

            {error && (
              <div
                className="text-sm text-alert bg-alert/10 border border-alert/30 rounded-lg px-3 py-2"
                data-testid="join-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-background rounded-lg py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              data-testid="join-submit"
            >
              {submitting ? 'Adding you…' : 'Get early access'}
            </button>

            <p className="text-xs text-text-muted text-center pt-2">
              No spam. Just an email when your spot opens up.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
